import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { getAnalyses } from '../services/api';
import { Layout } from '../components/layout/Layout';
import { CommunicationRadar } from '../components/dashboard/CommunicationRadar';
import { StatCard } from '../components/dashboard/StatCard';
import { DominantTraits } from '../components/dashboard/DominantTraits';
import { ExecutiveSummary } from '../components/dashboard/ExecutiveSummary';
import { CommunicationsTable } from '../components/dashboard/CommunicationsTable';
import { ClarityAnalysis } from '../components/dashboard/ClarityAnalysis';
import { LockedDashboard } from '../components/dashboard/LockedDashboard';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { ErrorDisplay } from '../components/common/ErrorDisplay';
import { InfoTooltip } from '../components/common/InfoTooltip';

export const Analytics: React.FC = () => {
  const navigate = useNavigate();

  console.log('🎯 Analytics: Component rendering');

  // Fetch all analyses for analytics stats
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['analytics-analyses'],
    queryFn: async () => {
      // Fetch a large page to get all analyses for stats
      const response = await getAnalyses(1, 1000);
      return response;
    },
    retry: 2, // Retry failed requests up to 2 times
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000), // Exponential backoff
  });

  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <LoadingSpinner 
            size="lg" 
            message="Loading analytics..." 
            showTimeoutMessage={true}
          />
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh] px-4">
          <ErrorDisplay
            type="network"
            title="Unable to Load Analytics"
            message="We couldn't load your analytics data. This might be due to a connection issue."
            onRetry={() => refetch()}
            onGoBack={() => navigate('/dashboard')}
            showRetry={true}
            showGoBack={true}
          />
        </div>
      </Layout>
    );
  }

  console.log('🎯 Analytics: Raw data from API:', data);
  
  // API returns { success: true, data: { analyses: [...], pagination: {...} } }
  const analyses = data?.data?.analyses || [];
  const totalAnalyses = analyses.length;
  
  console.log('🎯 Analytics: Analyses array:', analyses);
  console.log('🎯 Analytics: Total analyses:', totalAnalyses);

  // Calculate sentiment distribution
  const calculateSentimentDistribution = () => {
    if (analyses.length === 0) return { positive: 0, neutral: 0, negative: 0 };
    
    let totalPositive = 0;
    let totalNeutral = 0;
    let totalNegative = 0;
    
    analyses.forEach((analysis: any) => {
      const sentiment = analysis.sentiment;
      if (sentiment) {
        totalPositive += sentiment.positivity || 0;
        totalNeutral += sentiment.neutral || 0;
        totalNegative += sentiment.negativity || 0;
      }
    });
    
    // Calculate averages and convert to percentages
    const avgPositive = Math.round((totalPositive / analyses.length) * 100);
    const avgNeutral = Math.round((totalNeutral / analyses.length) * 100);
    const avgNegative = Math.round((totalNegative / analyses.length) * 100);
    
    return {
      positive: avgPositive,
      neutral: avgNeutral,
      negative: avgNegative
    };
  };

  // Calculate average positivity
  const getAveragePositivity = () => {
    if (analyses.length === 0) return 0;
    
    const total = analyses.reduce((sum: number, analysis: any) => {
      return sum + (analysis.sentiment?.positivity || 0);
    }, 0);
    
    // Convert to percentage and round
    const average = (total / analyses.length) * 100;
    return Math.round(average);
  };

  const sentimentDistribution = calculateSentimentDistribution();
  const averagePositivity = getAveragePositivity();

  console.log('🎯 Analytics: Sentiment distribution:', sentimentDistribution);

  // Determine dominant sentiment for display
  const getDominantSentiment = () => {
    const { positive, neutral, negative } = sentimentDistribution;
    
    if (positive >= neutral && positive >= negative) {
      return {
        label: 'Positive',
        value: positive,
        color: 'text-green-600',
        subtitle: `${neutral}% Neutral, ${negative}% Negative`
      };
    } else if (neutral >= positive && neutral >= negative) {
      return {
        label: 'Neutral',
        value: neutral,
        color: 'text-gray-600',
        subtitle: `${positive}% Positive, ${negative}% Negative`
      };
    } else {
      return {
        label: 'Negative',
        value: negative,
        color: 'text-red-600',
        subtitle: `${positive}% Positive, ${neutral}% Neutral`
      };
    }
  };

  const dominantSentiment = getDominantSentiment();

  // Show locked dashboard if user has < 10 analyses
  const REQUIRED_ANALYSES = 10;
  const isLocked = totalAnalyses < REQUIRED_ANALYSES;

  return (
    <Layout>
      {isLocked ? (
        <LockedDashboard
          currentCount={totalAnalyses}
          requiredCount={REQUIRED_ANALYSES}
          recentAnalyses={analyses.slice(0, 3).map((analysis: any) => ({
            id: analysis.id,
            text_snippet: analysis.text_snippet || '',
            date: analysis.date || new Date().toISOString(),
            sentiment_summary: analysis.sentiment_summary || 'Analysed',
          }))}
        />
      ) : (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header */}
          <div className="mb-6 sm:mb-8">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Analytics Dashboard</h1>
            <p className="text-sm sm:text-base text-gray-600">
              Comprehensive insights into your communication patterns and effectiveness
            </p>
          </div>

          {/* Empty State */}
          {totalAnalyses === 0 && (
        <div className="bg-white rounded-xl shadow-sm p-6 sm:p-8 md:p-12 text-center border border-gray-100">
          <svg className="mx-auto h-12 w-12 sm:h-16 sm:w-16 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
          <h3 className="mt-4 text-lg sm:text-xl font-semibold text-gray-900">No analyses yet</h3>
          <p className="mt-2 text-sm sm:text-base text-gray-600 max-w-md mx-auto">
            Start analysing your communications to see powerful insights about your communication style and patterns
          </p>
          <button
            onClick={() => navigate('/analyse')}
            className="mt-6 inline-flex items-center px-4 sm:px-6 py-2 sm:py-3 bg-indigo-600 text-white text-sm sm:text-base font-medium rounded-lg hover:bg-indigo-700 transition-colors"
          >
            <svg className="w-4 h-4 sm:w-5 sm:h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Analyse Communication
          </button>
        </div>
      )}

      {/* Analytics Content */}
      {totalAnalyses > 0 && (
        <>
          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            <StatCard
              title="Total Analyses"
              value={totalAnalyses}
              subtitle="All time"
              tooltipContent={[
                "Shows the total number of communications you've analyzed using the Comms Checker.",
                "Each analysis provides insights into your emotional tone, communication traits, and sentiment."
              ]}
              icon={
                <svg className="w-6 h-6 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              }
            />
            
            <StatCard
              title="Sentiment Balance"
              value={`${dominantSentiment.value}% ${dominantSentiment.label}`}
              subtitle={dominantSentiment.subtitle}
              tooltipContent={[
                "Shows your dominant sentiment category based on average scores across all communications.",
                "The percentage represents the average of your most common sentiment (Positive, Neutral, or Negative), with a breakdown of the other categories in the subtitle."
              ]}
              icon={
                <svg className="w-6 h-6 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              }
            />
            
            <StatCard
              title="Average Positivity"
              value={`${averagePositivity}%`}
              subtitle="Across all communications"
              tooltipContent={[
                "Your average positivity score across all analyzed communications.",
                "This metric specifically measures the positive sentiment in your messages, helping you track how upbeat and encouraging your communication style is."
              ]}
              icon={
                <svg className="w-6 h-6 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
              }
            />
          </div>

          {/* Executive Summary - Moved up */}
          <div className="mb-8">
            <ExecutiveSummary />
          </div>

          {/* Radar Chart */}
          <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6 mb-6 sm:mb-8 border border-gray-100">
            <div className="mb-4 sm:mb-6">
              <div className="flex items-start justify-between mb-2">
                <h2 className="text-lg sm:text-xl font-semibold text-gray-900">Communication Patterns</h2>
                <InfoTooltip
                  title="Communication Patterns"
                  content={[
                    "This radar chart visualizes your emotional communication profile across 8 key dimensions: COMMITTED, UPBEAT, CONTENT, COOL, DRAINED, DOWNBEAT, TROUBLED, and EXASPERATED.",
                    "Each dimension represents a group of related emotions analyzed from your communications. Larger areas indicate stronger presence of those emotional patterns.",
                    "Toggle between 'Average' view (your overall profile) and 'All' view (see individual analyses) using the buttons."
                  ]}
                  position="left"
                />
              </div>
              <p className="text-xs sm:text-sm text-gray-600">
                Your emotional communication profile across 8 key dimensions
              </p>
            </div>
            <CommunicationRadar analyses={analyses} />
          </div>

          {/* Dominant Traits and Clarity Analysis - 2 columns */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
            <DominantTraits analyses={analyses} />
            <ClarityAnalysis analyses={analyses} />
          </div>

          {/* Communications Table */}
          <div className="mb-8">
            <CommunicationsTable analyses={analyses} />
          </div>

          {/* Quick Actions */}
          <div className="bg-gradient-to-r from-indigo-50 to-blue-50 rounded-xl p-6 sm:p-8 border border-indigo-100">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-1 sm:mb-2">Ready for more insights?</h3>
                <p className="text-sm sm:text-base text-gray-600">Analyse another communication to refine your profile</p>
              </div>
              <button
                onClick={() => navigate('/analyse')}
                className="px-4 sm:px-6 py-2 sm:py-3 bg-indigo-600 text-white text-sm sm:text-base font-medium rounded-lg hover:bg-indigo-700 transition-colors whitespace-nowrap w-full sm:w-auto"
              >
                Analyse Communication
              </button>
            </div>
          </div>
          </>
        )}
        </div>
      )}
    </Layout>
  );
};
